import type { AgeBandRow, TermRow } from "@/src/childcare/types/childcare.types";
import { supabase } from "@/src/common/services/supabase.service";

export const childcareConfigService = {
	// --------------------------- terms ---------------------------
	async listTerms(): Promise<TermRow[]> {
		const { data, error } = await supabase.from("terms").select("*").order("start_date", { ascending: true });
		if (error) throw new Error(error.message);
		return data ?? [];
	},

	async createTerm(input: { name: string; start_date: string; end_date: string }): Promise<void> {
		const { error } = await supabase.from("terms").insert(input);
		if (error) throw new Error(error.message);
	},

	async updateTerm(id: string, input: { name: string; start_date: string; end_date: string }): Promise<void> {
		const { error } = await supabase.from("terms").update(input).eq("id", id);
		if (error) throw new Error(error.message);
	},

	async deleteTerm(id: string): Promise<void> {
		const { error } = await supabase.from("terms").delete().eq("id", id);
		if (error) throw new Error(error.message);
	},

	// --------------------------- age bands ---------------------------
	async listAgeBands(): Promise<AgeBandRow[]> {
		const { data, error } = await supabase.from("age_bands").select("*").order("sort_order", { ascending: true });
		if (error) throw new Error(error.message);
		return data ?? [];
	},

	/** Whether the current user can reach the childcare section (RLS-backed RPC). */
	async hasChildcareAccess(): Promise<boolean> {
		const { data, error } = await supabase.rpc("has_childcare_access");
		if (error) throw new Error(error.message);
		return data ?? false;
	},

	/** Schools the caller can see (RLS-scoped), for the Add-child school picker. */
	async listSchools(): Promise<{ id: string; name: string }[]> {
		const { data, error } = await supabase.from("schools").select("id, name").order("name", { ascending: true });
		if (error) throw new Error(error.message);
		return data ?? [];
	},

	/** Typeahead for linking a child to an enrolled student. */
	async searchStudents(
		search: string,
	): Promise<{ id: string; student_number: string; first_name: string | null; last_name: string | null; school_id: string | null }[]> {
		const token = search.trim().replace(/[,()*]/g, "");
		if (token.length === 0) return [];
		const { data, error } = await supabase
			.from("students")
			.select("id, student_number, first_name, last_name, school_id")
			.or(`first_name.ilike.%${token}%,last_name.ilike.%${token}%,student_number.ilike.%${token}%`)
			.order("last_name", { ascending: true })
			.limit(20);
		if (error) throw new Error(error.message);
		return data ?? [];
	},
};
